import { useMutation, useQuery } from '@tanstack/react-query';
import {
  formatMoney,
  QUOTE_LINK_LABELS,
  type QuoteLinkKind,
  type QuoteListItem,
  type QuoteResponse,
} from '@k-management/shared';
import { useState } from 'react';
import { Link } from 'react-router';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import { Field } from '../../components/Field.js';
import { apiRequest } from '../../lib/api.js';
import { formatDate } from '../../lib/format.js';
import { quoteLabel, QuoteStatePill } from './quotes-ui.js';

const KIND_CLASS: Record<QuoteLinkKind, string> = {
  origin: 'brand',
  copy: 'brand',
  'revision-of': 'muted',
  'replaced-by': 'muted',
  manual: 'ok',
};

/**
 * The quotes tied to this one, both ways (decision of 26/09/2026): the origin
 * of a copy and the copies made from it, the revisions, and the links made by
 * hand with their note. A quote the user does not see stays anonymous.
 */
export function LinksView({
  quote,
  canWrite,
  onLink,
  onChanged,
}: {
  quote: QuoteResponse;
  canWrite: boolean;
  onLink: () => void;
  onChanged: (updated: QuoteResponse) => Promise<void>;
}) {
  const [error, setError] = useState('');
  const unlink = useMutation({
    mutationFn: (linkId: string) =>
      apiRequest<QuoteResponse>(`/quotes/${quote.id}/links/${linkId}`, { method: 'DELETE' }),
    onSuccess: async (updated) => {
      setError('');
      await onChanged(updated);
    },
    onError: (failure: Error) => setError(failure.message),
  });

  return (
    <section className="card stack">
      <div className="row between">
        <h2>Preventivi collegati</h2>
        {canWrite && (
          <button type="button" onClick={onLink} disabled={!quote.clientId}>
            Collega preventivo
          </button>
        )}
      </div>
      {canWrite && !quote.clientId && (
        <p className="muted small">
          Si collegano solo preventivi dello stesso cliente: scegli prima il cliente di questo.
        </p>
      )}
      {quote.links.length === 0 ? (
        <p className="muted">Nessun preventivo collegato.</p>
      ) : (
        <div className="table-scroll">
          <table className="lines">
            <thead>
              <tr>
                <th>Legame</th>
                <th>Preventivo</th>
                <th>Data</th>
                <th>Stato</th>
                <th className="right">Imponibile</th>
                <th>Nota</th>
                {canWrite && <th aria-label="Azioni" />}
              </tr>
            </thead>
            <tbody>
              {quote.links.map((link, index) => (
                <tr key={link.id ?? `${link.kind}-${index}`}>
                  <td>
                    <span className={`pill ${KIND_CLASS[link.kind]}`}>
                      {QUOTE_LINK_LABELS[link.kind]}
                    </span>
                  </td>
                  {link.quote ? (
                    <>
                      <td>
                        <Link to={`/preventivatore/${link.quote.id}`}>
                          {quoteLabel(link.quote)}
                        </Link>
                      </td>
                      <td>{formatDate(link.quote.date)}</td>
                      <td>
                        <QuoteStatePill state={link.quote.state} expired={link.quote.expired} />
                      </td>
                      <td className="right">{formatMoney(link.quote.net)}</td>
                    </>
                  ) : (
                    <td colSpan={4} className="muted">
                      Un preventivo che il tuo profilo non vede
                    </td>
                  )}
                  <td>{link.note ?? <span className="muted">—</span>}</td>
                  {canWrite && (
                    <td className="row-actions">
                      {link.kind === 'manual' && link.id && (
                        <button
                          type="button"
                          className="small"
                          disabled={unlink.isPending}
                          onClick={() => unlink.mutate(link.id!)}
                        >
                          Scollega
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {error && <p className="error">{error}</p>}
      <p className="muted small">
        I collegamenti valgono nei due sensi. L’origine di una copia e le revisioni non si
        scollegano: sono la storia del documento.
      </p>
    </section>
  );
}

/** Links by hand another quote of the same client, with a note. */
export function LinkDialog({
  quote,
  onClose,
  onDone,
}: {
  quote: QuoteResponse;
  onClose: () => void;
  onDone: (updated: QuoteResponse) => Promise<void>;
}) {
  const candidates = useQuery({
    queryKey: ['quotes', quote.id, 'linkable'],
    queryFn: () => apiRequest<QuoteListItem[]>(`/quotes/${quote.id}/linkable`),
  });
  const [search, setSearch] = useState('');
  const [chosen, setChosen] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const save = useMutation({
    mutationFn: () =>
      apiRequest<QuoteResponse>(`/quotes/${quote.id}/links`, {
        method: 'POST',
        body: { quoteId: chosen, note: note || null },
      }),
    onSuccess: (updated) => onDone(updated),
    onError: (failure: Error) => setError(failure.message),
  });
  const words = search.trim().toLowerCase();
  const shown = (candidates.data ?? []).filter(
    (item) => !words || quoteLabel(item).toLowerCase().includes(words),
  );

  return (
    <ConfirmDialog
      title="Collega un preventivo"
      confirmLabel="Collega"
      busy={save.isPending}
      error={error}
      onConfirm={() => (chosen ? save.mutate() : setError('Scegli il preventivo da collegare'))}
      onCancel={onClose}
    >
      <p className="muted small">
        Preventivi di <strong>{quote.clientName}</strong> che puoi vedere e non ancora collegati.
      </p>
      <Field label="Cerca">
        <input value={search} onChange={(event) => setSearch(event.target.value)} />
      </Field>
      {candidates.isPending && <p className="muted">Caricamento…</p>}
      {candidates.data && shown.length === 0 && (
        <p className="muted">Nessun altro preventivo di questo cliente da collegare.</p>
      )}
      <div className="pick-list" role="radiogroup" aria-label="Preventivo da collegare">
        {shown.map((item) => (
          <label key={item.id} className={chosen === item.id ? 'pick selected' : 'pick'}>
            <input
              type="radio"
              name="linked-quote"
              checked={chosen === item.id}
              onChange={() => setChosen(item.id)}
            />
            <span>
              <strong>{quoteLabel(item)}</strong> · {formatDate(item.date)}
            </span>
            <QuoteStatePill state={item.state} expired={item.expired} />
            <span className="right">{formatMoney(item.net)}</span>
          </label>
        ))}
      </div>
      <Field label="Nota (facoltativa)" hint="Per esempio: fase 2, estensione, alternativa B">
        <input maxLength={200} value={note} onChange={(event) => setNote(event.target.value)} />
      </Field>
    </ConfirmDialog>
  );
}

/**
 * Duplica (decision of 26/09/2026): two questions, what to copy and whether to
 * link the copy to this quote.
 */
export function DuplicateDialog({
  quote,
  canCosts,
  onClose,
  onDone,
}: {
  quote: QuoteResponse;
  /** Who does not see the costs is not asked: the costs travel with the copy. */
  canCosts: boolean;
  onClose: () => void;
  onDone: (created: QuoteResponse) => Promise<void>;
}) {
  const [withCosts, setWithCosts] = useState(false);
  const [link, setLink] = useState(true);
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const save = useMutation({
    mutationFn: () =>
      apiRequest<QuoteResponse>(`/quotes/${quote.id}/duplicate`, {
        method: 'POST',
        body: { withCosts: canCosts ? withCosts : true, link, note: link ? note || null : null },
      }),
    onSuccess: (created) => onDone(created),
    onError: (failure: Error) => setError(failure.message),
  });

  return (
    <ConfirmDialog
      title={`Duplica ${quoteLabel(quote)}`}
      confirmLabel="Duplica"
      busy={save.isPending}
      error={error}
      onConfirm={() => save.mutate()}
      onCancel={onClose}
    >
      {canCosts && (
        <fieldset className="choices">
          <legend>Cosa vuoi duplicare?</legend>
          <label className={!withCosts ? 'choice selected' : 'choice'}>
            <input type="radio" checked={!withCosts} onChange={() => setWithCosts(false)} />
            <span>
              <strong>Solo ricavi</strong>
              <small className="muted block">
                Righe, sconti e licenze. I costi previsti ripartono da scegliere.
              </small>
            </span>
          </label>
          <label className={withCosts ? 'choice selected' : 'choice'}>
            <input type="radio" checked={withCosts} onChange={() => setWithCosts(true)} />
            <span>
              <strong>Ricavi e costi</strong>
              <small className="muted block">Anche i costi scelti, con i valori di oggi.</small>
            </span>
          </label>
        </fieldset>
      )}
      <fieldset className="choices">
        <legend>Vuoi collegare il nuovo preventivo a questo?</legend>
        <label className={link ? 'choice selected' : 'choice'}>
          <input type="radio" checked={link} onChange={() => setLink(true)} />
          <span>
            <strong>Sì, collega</strong>
            <small className="muted block">
              Nel nuovo preventivo comparirà «duplicato da {quoteLabel(quote)}», anche se cambi
              cliente.
            </small>
          </span>
        </label>
        <label className={!link ? 'choice selected' : 'choice'}>
          <input type="radio" checked={!link} onChange={() => setLink(false)} />
          <span>
            <strong>No</strong>
            <small className="muted block">Il nuovo preventivo è indipendente.</small>
          </span>
        </label>
      </fieldset>
      {link && (
        <Field label="Nota sul collegamento (facoltativa)">
          <input
            maxLength={200}
            value={note}
            placeholder="Per esempio: versione con due giornate in più"
            onChange={(event) => setNote(event.target.value)}
          />
        </Field>
      )}
      <p className="muted small">Il nuovo preventivo nasce come bozza, con lo stesso cliente.</p>
    </ConfirmDialog>
  );
}
